import { ref, readonly, onMounted, onUnmounted, inject } from 'vue';
import { useConvexClient } from 'convex-vue';
import { ConvexHttpClient } from 'convex/browser';
import type { Value } from 'convex/values';
import { useRouter } from 'vue-router';

const VERIFIER_STORAGE_KEY = '__convexAuthOAuthVerifier';
const JWT_STORAGE_KEY = '__convexAuthJWT';
const REFRESH_TOKEN_STORAGE_KEY = '__convexAuthRefreshToken';
const REDIRECT_URL_STORAGE_KEY = '__convexAuthRedirectUrl';
const PENDING_INVITE_STORAGE_KEY = '__convexAuthPendingInviteCode';

// Global auth state (singleton)
const isAuthenticated = ref(false);
const isLoading = ref(true);
const token = ref<string | null>(null);

let initialized = false;

function getStorageKey(key: string, namespace: string): string {
  if (!namespace) return key;
  const escapedNamespace = namespace.replace(/[^a-zA-Z0-9]/g, '');
  return `${key}_${escapedNamespace}`;
}

interface ConvexVueContext {
  options: { url: string };
}

// Track which clients have been initialized
const initializedClients = new WeakSet<object>();

export function useConvexAuth() {
  const client = useConvexClient();
  const context = inject<ConvexVueContext>('convex-vue');
  const convexUrl = context?.options?.url ?? import.meta.env.VITE_CONVEX_URL ?? '';

  let router: ReturnType<typeof useRouter> | null = null;
  try {
    router = useRouter();
  } catch {
    // Router not available
  }

  const storageGet = (key: string): string | null => {
    try {
      return localStorage.getItem(getStorageKey(key, convexUrl));
    } catch {
      return null;
    }
  };

  const storageSet = (key: string, value: string): void => {
    try {
      localStorage.setItem(getStorageKey(key, convexUrl), value);
    } catch {
      // Storage not available
    }
  };

  const storageRemove = (key: string): void => {
    try {
      localStorage.removeItem(getStorageKey(key, convexUrl));
    } catch {
      // Storage not available
    }
  };

  const sanitizeRedirectUrl = (url: string | null | undefined): string | null => {
    if (!url) return null;

    const trimmedUrl = url.trim();
    if (!trimmedUrl) return null;
    if (/[\u0000-\u001F\u007F]/.test(trimmedUrl)) return null;

    if (typeof window === 'undefined') {
      return trimmedUrl.startsWith('/') && !trimmedUrl.startsWith('//') ? trimmedUrl : null;
    }

    try {
      const normalized = trimmedUrl.startsWith('/')
        ? new URL(trimmedUrl, window.location.origin)
        : new URL(trimmedUrl);

      if (normalized.origin !== window.location.origin) return null;

      const sanitized = `${normalized.pathname}${normalized.search}${normalized.hash}`;
      if (!sanitized.startsWith('/') || sanitized.startsWith('//')) return null;

      return sanitized;
    } catch {
      return null;
    }
  };

  const getInviteCodeFromRedirect = (url: string | null | undefined): string | null => {
    const sanitized = sanitizeRedirectUrl(url);
    if (!sanitized) return null;

    const inviteMatch = sanitized.match(/^\/invite\/([^/?#]+)/);
    if (!inviteMatch?.[1]) return null;

    try {
      return decodeURIComponent(inviteMatch[1]);
    } catch {
      return inviteMatch[1];
    }
  };

  const storePendingInviteCode = (code: string): void => {
    if (!code) return;
    storageSet(PENDING_INVITE_STORAGE_KEY, code);
  };

  const getPendingInviteCode = (): string | null => {
    return storageGet(PENDING_INVITE_STORAGE_KEY);
  };

  const clearPendingInviteCode = (): void => {
    storageRemove(PENDING_INVITE_STORAGE_KEY);
  };

  // Store redirect URL for after authentication
  const storeRedirectUrl = (url: string): void => {
    const sanitized = sanitizeRedirectUrl(url);
    if (!sanitized) {
      storageRemove(REDIRECT_URL_STORAGE_KEY);
      return;
    }

    storageSet(REDIRECT_URL_STORAGE_KEY, sanitized);

    const inviteCode = getInviteCodeFromRedirect(sanitized);
    if (inviteCode) {
      storePendingInviteCode(inviteCode);
    }
  };

  // Get and clear stored redirect URL
  const getAndClearRedirectUrl = (): string | null => {
    const url = sanitizeRedirectUrl(storageGet(REDIRECT_URL_STORAGE_KEY));
    if (url) {
      storageRemove(REDIRECT_URL_STORAGE_KEY);

      const inviteCode = getInviteCodeFromRedirect(url);
      if (inviteCode) {
        storePendingInviteCode(inviteCode);
      }
    }
    return url;
  };

  const setToken = (
    tokens: { token: string; refreshToken?: string } | null,
    shouldStore = true
  ) => {
    if (tokens === null) {
      token.value = null;
      isAuthenticated.value = false;
      client.setAuth(async () => null);
      if (shouldStore) {
        storageRemove(JWT_STORAGE_KEY);
        storageRemove(REFRESH_TOKEN_STORAGE_KEY);
      }
    } else {
      token.value = tokens.token;
      isAuthenticated.value = true;
      client.setAuth(async () => tokens.token);
      if (shouldStore) {
        storageSet(JWT_STORAGE_KEY, tokens.token);
        if (tokens.refreshToken) {
          storageSet(REFRESH_TOKEN_STORAGE_KEY, tokens.refreshToken);
        }
      }
    }
    isLoading.value = false;
  };

  // SYNCHRONOUS initialization - set auth from localStorage immediately
  // This ensures the client has auth before any queries run
  // We check the client instance to handle cases where the same client is used
  if (typeof window !== 'undefined' && !initializedClients.has(client)) {
    initializedClients.add(client);
    const storedToken = storageGet(JWT_STORAGE_KEY);
    if (storedToken) {
      token.value = storedToken;
      isAuthenticated.value = true;
      client.setAuth(async () => storedToken);
      isLoading.value = false;
    } else {
      isLoading.value = false;
    }
  }

  const signIn = async (
    provider: string,
    params?: Record<string, Value> & { redirectTo?: string; code?: string }
  ): Promise<{ signingIn: boolean; redirect?: URL }> => {
    const verifier = storageGet(VERIFIER_STORAGE_KEY) ?? undefined;
    storageRemove(VERIFIER_STORAGE_KEY);

    const result = (await client.action(
      'auth:signIn' as never,
      {
        provider,
        params: params ?? {},
        verifier,
      } as never
    )) as {
      redirect?: string;
      verifier?: string;
      tokens?: { token: string; refreshToken: string } | null;
    };

    if (result.redirect !== undefined) {
      const url = new URL(result.redirect);
      if (result.verifier) {
        storageSet(VERIFIER_STORAGE_KEY, result.verifier);
      }
      window.location.href = url.toString();
      return { signingIn: false, redirect: url };
    } else if (result.tokens !== undefined) {
      setToken(result.tokens);
      return { signingIn: result.tokens !== null };
    }

    return { signingIn: false };
  };

  const signOut = async (): Promise<void> => {
    try {
      await client.action('auth:signOut' as never, {} as never);
    } catch {
      // Ignore errors - usually already signed out
    }
    setToken(null);
  };

  const refreshTokenFn = async (): Promise<boolean> => {
    const storedRefreshToken = storageGet(REFRESH_TOKEN_STORAGE_KEY);
    if (!storedRefreshToken) {
      setToken(null, false);
      return false;
    }

    try {
      const httpClient = new ConvexHttpClient(convexUrl);
      const result = (await httpClient.action(
        'auth:signIn' as never,
        {
          refreshToken: storedRefreshToken,
        } as never
      )) as { tokens?: { token: string; refreshToken: string } | null };

      if (result.tokens) {
        setToken(result.tokens);
        return true;
      }
    } catch {
      // Refresh failed
    }

    setToken(null);
    return false;
  };

  // Handle code from URL (for OAuth and magic links)
  const handleCodeFromURL = async (): Promise<boolean> => {
    if (typeof window === 'undefined') return false;

    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');

    if (!code) return false;

    // Remove code from URL immediately to prevent re-processing
    const url = new URL(window.location.href);
    url.searchParams.delete('code');
    const callbackPathAfterCodeRemoval = sanitizeRedirectUrl(
      `${url.pathname}${url.search}${url.hash}`
    );
    window.history.replaceState({}, '', url.pathname + url.search + url.hash);

    // If already authenticated, just redirect without re-verifying
    // This handles the case where user clicks magic link but is already logged in
    if (isAuthenticated.value && token.value) {
      const storedRedirect = getAndClearRedirectUrl();
      const redirectTarget = storedRedirect || callbackPathAfterCodeRemoval || '/workshops';

      const inviteCode = getInviteCodeFromRedirect(redirectTarget);
      if (inviteCode) {
        storePendingInviteCode(inviteCode);
      }

      if (router) {
        await router.push(redirectTarget);
      } else {
        window.location.href = redirectTarget;
      }
      return true;
    }

    const verifier = storageGet(VERIFIER_STORAGE_KEY) ?? undefined;
    storageRemove(VERIFIER_STORAGE_KEY);

    try {
      const httpClient = new ConvexHttpClient(convexUrl);
      const result = (await httpClient.action(
        'auth:signIn' as never,
        {
          params: { code },
          verifier,
        } as never
      )) as { tokens?: { token: string; refreshToken: string } | null };

      if (result.tokens) {
        setToken(result.tokens);

        // Get stored redirect URL (from participant login flow)
        const storedRedirect = getAndClearRedirectUrl();

        // CRITICAL: Check if user needs name setup BEFORE redirecting
        // We use the HTTP client directly with the new token to avoid race conditions
        const httpClient = new ConvexHttpClient(convexUrl);
        httpClient.setAuth(result.tokens.token);
        const api = await import('../../convex/_generated/api');

        try {
          const currentUser = await httpClient.query(api.api.auth.currentUser);
          const needsNameSetup = currentUser && !currentUser.name;

          if (needsNameSetup) {
            // Store redirect URL for after name setup (unless it's already a setup route)
            if (
              storedRedirect &&
              storedRedirect !== '/setup-name' &&
              storedRedirect !== '/workshops'
            ) {
              storageSet(REDIRECT_URL_STORAGE_KEY, storedRedirect);
            }
            // Extract invite token from storedRedirect to pass to name setup
            let inviteParam: { query?: { invite: string } } = {};
            const invite = getInviteCodeFromRedirect(storedRedirect);
            if (invite) {
              inviteParam = { query: { invite } };
            }
            if (router) {
              await router.push({ path: '/setup-name', ...inviteParam });
            } else {
              const inviteQuery = inviteParam.query ? `?invite=${inviteParam.query.invite}` : '';
              window.location.href = `/setup-name${inviteQuery}`;
            }
            return true;
          }
        } catch (error) {
          console.error('[Auth] Failed to check user name:', error);
        }

        // Determine redirect target:
        // 1. Go to stored redirect URL
        // 2. Use callback path if available (covers lost storage)
        // 3. Fallback to /workshops
        const redirectTarget = storedRedirect || callbackPathAfterCodeRemoval || '/workshops';

        const inviteCode = getInviteCodeFromRedirect(redirectTarget);
        if (inviteCode) {
          storePendingInviteCode(inviteCode);
        }

        if (router) {
          await router.push(redirectTarget);
        } else {
          window.location.href = redirectTarget;
        }
        return true;
      } else {
        // tokens is null - this could mean the code was invalid or already used
        // Clear any stale verifier and redirect to login
        storageRemove(VERIFIER_STORAGE_KEY);
      }
    } catch (error) {
      console.error('[Auth] Failed to verify code:', error);
    }

    return false;
  };

  // Validate that the stored token is still valid
  const validateToken = async (): Promise<boolean> => {
    const storedToken = storageGet(JWT_STORAGE_KEY);
    if (!storedToken) return false;

    try {
      const httpClient = new ConvexHttpClient(convexUrl);
      httpClient.setAuth(storedToken);
      const api = await import('../../convex/_generated/api');
      await httpClient.query(api.api.auth.currentUser);
      return true;
    } catch (error: any) {
      // Check if it's an auth error (401 or auth-related error)
      const errorMessage = error?.message || '';
      if (
        errorMessage.includes('NoAuthProvider') ||
        errorMessage.includes('Unauthorized') ||
        errorMessage.includes('Unauthenticated') ||
        errorMessage.includes('401') ||
        errorMessage.includes('expired')
      ) {
        console.log('[Auth] Token validation failed, attempting refresh...');
        // Try to refresh the token before clearing it
        const refreshed = await refreshTokenFn();
        if (refreshed) {
          return true;
        }
        setToken(null);
        return false;
      }
      // Other errors (network, etc.) - don't clear token
      return true;
    }
  };

  // Async initialization for code handling and token validation
  const initialize = async () => {
    if (initialized) return;

    initialized = true;

    // Check for code in URL (magic link / OAuth callback)
    const hasCode = typeof window !== 'undefined' && window.location.search.includes('code=');
    if (hasCode) {
      const handledCode = await handleCodeFromURL();
      if (handledCode) {
        return;
      }
    }

    // If we have a token from sync init, validate it
    if (token.value) {
      const isValid = await validateToken();
      if (!isValid) {
        // Reload to trigger router redirect to login
        window.location.reload();
        return;
      }
    }

    // If we didn't already load from storage in sync init, mark as done
    if (!token.value) {
      isLoading.value = false;
    }
  };

  // Listen for storage changes (cross-tab sync)
  const handleStorageChange = (event: StorageEvent) => {
    if (event.key === getStorageKey(JWT_STORAGE_KEY, convexUrl)) {
      const value = event.newValue;
      if (value === null) {
        setToken(null, false);
      } else {
        setToken({ token: value }, false);
      }
    }
  };

  onMounted(() => {
    initialize();
    window.addEventListener('storage', handleStorageChange);
  });

  onUnmounted(() => {
    window.removeEventListener('storage', handleStorageChange);
  });

  return {
    isAuthenticated: readonly(isAuthenticated),
    isLoading: readonly(isLoading),
    token: readonly(token),
    signIn,
    signOut,
    refreshToken: refreshTokenFn,
    storeRedirectUrl,
    getAndClearRedirectUrl,
    getPendingInviteCode,
    storePendingInviteCode,
    clearPendingInviteCode,
    sanitizeRedirectUrl,
  };
}
